import { getRequest, postRequest, retryUntilSuccessOrFailure } from '../utils'

export const getMemberCheckSettings: RequestTypeVariable = {
  getKey: id => ['getMemberCheckSettings', id],
  request: async (id: string, signal: AbortSignal): Promise<SettingsOnMember[]> => {
    const { data } = await getRequest<SettingsOnMember[]>({
      url: `/projects/${id}/member-check-settings`,
      signal,
    })

    return data as SettingsOnMember[]
  },
}

export const getAnchorChecks: RequestTypeVariable = {
  getKey: id => ['getAnchorChecks', id],
  request: async (id: string, signal: AbortSignal): Promise<AnchorCheckOnSupport[]> => {
    const { data } = await getRequest<AnchorCheckOnSupport[]>({
      url: `/projects/${id}/decisive-anchor-checks`,
      signal,
    })

    return data as AnchorCheckOnSupport[]
  },
}

export interface SupportingElementCSConfig {
  element_guid: string
  cross_section: CrossSection
}

export interface GetCalcChecksForElement {
  element_guid: string
  crossSection: CrossSectionTimberOrCLTOrSteel
  settings: SettingsOnMember
  supporting_elements_cs_config: SupportingElementCSConfig[]
}

export const getCalcChecksForElementRequest2: RequestTypeVariable = {
  getKey: (projectId: string, element: GetCalcChecksForElement) => [
    'getCalcChecksForElement',
    projectId,
    element.element_guid,
  ],
  request: async (
    projectId: string,
    {
      requestData,
      options,
    }: {
      requestData: GetCalcChecksForElement
      options?: {
        signal?: AbortSignal
      }
    },
  ) => {
    const { data } = await postRequest<CrossSectionSuggestion>({
      url: `/projects/${projectId}/calc-element-checks`,
      data: requestData,
      signal: options?.signal,
    })
    return data as CrossSectionSuggestion
  },
}

export const getCalcChecksForElement = {
  getKey: (
    id: string,
    elementGuid: string,
    settings: SettingsOnMember,
    crossSection: CrossSection,
    supportingElementsCSConfig?: SupportingElementCSConfig[],
  ) => [
    'getCalcChecksForElement',
    id,
    elementGuid,
    JSON.stringify(settings),
    JSON.stringify(crossSection),
    supportingElementsCSConfig ? JSON.stringify(supportingElementsCSConfig) : '',
  ],
  request: async (
    projectId: string,
    elementGuid: string,
    settings: SettingsOnMember,
    crossSection: CrossSectionTimberOrCLTOrSteel,
    options?: {
      signal?: AbortSignal
      supporting_elements_cs_config?: SupportingElementCSConfig[]
    },
  ): Promise<CrossSectionSuggestion> => {
    const dataForRequest = {
      element_guid: elementGuid,
      settings: settings,
      crossSection: crossSection,
      supporting_elements_cs_config: options?.supporting_elements_cs_config ?? [],
    }

    const { data } = await postRequest<CrossSectionSuggestion>({
      url: `/projects/${projectId}/calc-element-checks`,
      data: dataForRequest,
      signal: options?.signal,
    })

    return data as CrossSectionSuggestion
  },
}

export const getDefaultCheckSettingsForElement = {
  getKey: (id: string, elementGuid: string, newCS: CrossSectionTimberOrCLTOrSteel) => [
    'getDefaultCheckSettingsForElement',
    id,
    elementGuid,
    JSON.stringify(newCS),
  ],
  request: async (
    projectId: string,
    elementGuid: string,
    newCS: CrossSectionTimberOrCLTOrSteel,
  ): Promise<GetDefaultCheckSettingsForElementResponse> => {
    const dataForRequest = {
      new_cs: newCS,
    }

    const { data } = await postRequest<GetDefaultCheckSettingsForElementResponse>({
      url: `/projects/${projectId}/updated-check-settings/${elementGuid}`,
      data: dataForRequest,
    })

    return data as GetDefaultCheckSettingsForElementResponse
  },
}

export const postCalcPositionChecks: RequestTypeVariable = {
  getKey: (projectId: string) => ['postCalcPositionChecks', projectId],
  request: async (id: string, elementGuids: string[]): Promise<void> => {
    const { data } = await postRequest<{ task_id: string }>({
      url: `/projects/${id}/calc-position-checks/compute`,
      data: {
        elements: elementGuids,
      },
    })

    await retryUntilSuccessOrFailure({
      url: `/task-status/${data?.task_id}`,
    })
  },
}
